// Where work and money ENTER: the raw task's admission trail, the donation
// lifecycle a public repo is funded by, the grants it issues, and the API tokens
// a caller reaches all of it with.
// Split out of `../audit.ts`, which is now the barrel over this directory. Not in
// the barrel: this is a HALF of `AuditEventKind`, exported only so `./kind` can
// assemble it. Import `AuditEventKind` — never a group.

export const AUDIT_KIND_COMMERCE = {
  // arc 101 doc 01 (#2311): the RAW TASK's admission trail. A raw task is the
  // object that means "this should happen; nobody has decided how or whether
  // yet" (`docs/idea/lifecycle.md` § "The spine"), and its entire value is that
  // the decision to admit or discard an ask is now a recorded act instead of a
  // side effect of whichever inlet happened to mint a `tasks` row. Sixteen inlets
  // minted work and one audited gate covered ~1% of them (#2117); these four rows
  // are what closes that.
  //   `raw_task.created` — an inlet filed an ask. Written ONLY for a genuinely new
  //     row: `createRawTask` answers a discriminated `{kind:'created'|'existing'}`
  //     verdict, and a redelivery (`existing`) is not a state change, so it audits
  //     nothing. The row names the source, the repos, the dedupe key and the
  //     origin ref — never the ask's pasted body, which stays in `inbound_events`.
  //   `raw_task.triaged` / `raw_task.dropped` / `raw_task.escalated` — the settle,
  //     one kind per terminal status. FIRST-CLASS kinds rather than one
  //     `raw_task.settled` carrying the outcome in a payload: "what did we refuse
  //     to do, and why" is the question this object exists to make answerable, and
  //     an operator has to be able to read it off the INDEXED `kind` column. The
  //     three are exhaustive over `SettledRawTaskStatus` by construction — the
  //     settle looks the kind up through a `Record<SettledRawTaskStatus,
  //     AuditEventKind>`, so a fifth status cannot ship without a fifth row here.
  // Parent-less (a queue/webhook lane, no `agent_sessions` row), each anchored to
  // its ACCOUNT like the repo rows above. `raw_task.created` has ONE builder,
  // `@developerz/audit` `rawTaskCreatedDraft` (plan 101 11-4, #4770), with two
  // callers: the raw-task door (`packages/audit/src/raw-task-door.ts`), which every
  // app's inlets file through and whose audit port is that app's tenant-anchored
  // sink; and the blocker answer (`apps/api/src/deps/blockers.ts`), whose
  // resumption ask is filed inside the answer transaction and so cannot use the
  // door. Grep `rawTaskCreatedDraft(` outside tests to re-check the list.
  RawTaskCreated: 'raw_task.created',
  RawTaskTriaged: 'raw_task.triaged',
  RawTaskDropped: 'raw_task.dropped',
  RawTaskEscalated: 'raw_task.escalated',
  // Plan 101 slice 08: the settle row for `awaiting_info` — triage returned
  // `needs_info`, a blocker carries the questions (and, for a `gh_issue` ask, a
  // disclosed comment on the issue), and the ask is parked on the asker. Its own
  // kind rather than riding `raw_task.escalated`: nobody escalated anything, and
  // "which asks are waiting on their reporter" must be readable off `kind`. The
  // fourth member of `RAW_TASK_SETTLE_AUDIT_KIND`, which is total over
  // `SettledRawTaskStatus` and so refused to compile without it.
  RawTaskAwaitingInfo: 'raw_task.awaiting_info',
  // #7649: what an escalate's DELIVERY LEG did, AFTER its settle won — the follow-up
  // to `raw_task.escalated` from a settler that TELLS, whose settle carries `delivery:
  // 'pending'` (it settles before anyone is told, so a fault before the settle reaches
  // nobody; the settlers are listed in `TriageEscalationDelivery`'s docblock). Not a
  // settle kind. `input.rawTaskId` names the ask; `output` carries `{ delivery }` —
  // `queued` or `undelivered`. Written in the SAME transaction as the fenced `pending`
  // -> outcome write on `raw_tasks.verdict` (`recordRawTaskEscalationDelivery`), so the
  // row exists iff the outcome landed (#7653). Rows written before #7653 also carry a
  // `recorded` boolean, from when the two were separate writes; no reader reads it.
  RawTaskEscalationDelivered: 'raw_task.escalation_delivered',
  // Plan 101 L1: a RECOVERABLE tenant gate (billing paused, a key still verifying,
  // parked or inactive) HELD the ask rather than settling it. Before this, the gate
  // churned the claim five times and settled `attempts_exhausted` with no audit row:
  // 222 of 237 such settles in 30 days sat on one `no_byok` account. NOT a settle kind,
  // and deliberately outside `RAW_TASK_SETTLE_AUDIT_KIND`: the ask stays unsettled and
  // uncharged until the key or billing signal (or the re-check) re-drives it. Its own
  // kind so "which asks wait on their account" reads off the indexed `kind` column.
  // `input.rawTaskId` names the ask; `output.reason` names the gate.
  RawTaskHeld: 'raw_task.held',
  // #5387: triage DEFERRED the ask behind a person's open pull request (the `defer`
  // decision, `packages/agent/src/triage/human-work.ts`) — held `human_in_flight` and
  // re-judged later, never settled. Its own kind rather than `raw_task.held`: a hold is a
  // gate the account owes a fix for, this is a judgement that a human already has the
  // work. `input.rawTaskId` names the ask; `output` carries `{ pr_number, repo, reason }`.
  RawTaskDeferred: 'raw_task.deferred',
  // H3 #5393 (plan 101 slice 09): the intake SCREEN read one ask's text before triage
  // did. EVERY screen writes one — `ok` and `discard`, fresh or a `screen_verdicts` hit
  // — because "was this ask screened, and by which prompt" must be answerable for every
  // ask that reached triage, not only the refused ones. Not a settle kind: a discard
  // ALSO settles `raw_task.dropped` (the `discarded` verdict); this row is the screen's
  // own record. `input.rawTaskId` names the ask; `output` carries `{ verdict, reason,
  // prompt_version, cached }` — never the text.
  RawTaskScreened: 'raw_task.screened',
  // H6 #5420 (plan 101 slice 09): triage's answer to "is this report TRUE?" for ONE
  // judged ask — `confirmed` | `refuted` | `unverifiable`, graded from what the prompt
  // rendered — and what the trust floor did with it (`packages/agent/src/triage/
  // claim.ts`). Not a settle kind: the verdict's own row settles. `input.rawTaskId`
  // names the ask; `output` carries `{ claim, basis, trust, floor_verdict, floor }`.
  // `floor_verdict` is the verdict the FLOOR left, written before any effect — not the
  // final one: adjudication may still refuse it, and `triage.verdict` records what landed.
  RawTaskClaim: 'raw_task.claim',
  // #7082: a still-UNCLAIMED ask was AMENDED in place — a scheduled checkup reported a
  // second subject on a brief row whose ask was open, and the subject, its paths and its
  // evidence were appended (`@developerz/db` `amendPendingRawTaskAsk`, the row committed
  // in the amend's own transaction). Not a refusal, so not `policy.gate.refused`, and
  // not a settle kind: the ask stays pending and is judged with its new text.
  // `input.rawTaskId` names the ask; `input` carries the anchor, the open ask's dedupe
  // key, the paths and the evidence lines.
  RawTaskAmended: 'raw_task.amended',
  // #2270: an operator / control-plane verb that was filed as `mcp_tool.call.*` with its
  // name in `tool`. The value IS that name, so a new row's `kind` equals the old rows'
  // `tool`. For a name that was NEVER split, one query reads both:
  // `kind = $1 OR (kind LIKE 'mcp_tool.call.%' AND tool = $1)`. A name #6124 split by
  // verdict (an ok/err pair, or `plan.file_refused`) has its own reads, beside it.
  // An operator re-drove an ask (or was refused), and an ask whose raw task could not
  // be nudged (`deps/asks.ts`).
  AskRedriven: 'ask.redriven',
  AskRedriveRefused: 'ask.redrive_refused',
  AskRawTaskUnnudged: 'ask.raw_task_unnudged',
  // #7303: the ask NAMED an issue and the platform did NOT link it — the row that
  // says so, with the reason. The ask's structured issue reaches the plan as a
  // number the runner's footer renders as `Closes #<n>`, so a link that is NOT
  // made is a silent gap: the pull request merges and the issue stays open, with
  // nothing on the trail saying the platform decided that. This row is that
  // decision, written by the triage lane's issue-link step
  // (`packages/agent/src/triage/issue-link.ts`) on the account-anchored sink the
  // lane's other rows use.
  //   `reason` — WHY no piece carries the number. `multiple_pr_tasks`: the ask's
  //     plan holds TWO OR MORE pull-request pieces in the issue's repository, so
  //     no single pull request owns the issue's closure and the platform links
  //     NONE of them rather than pick one.
  // The issue itself rides `output.issue` (the ask's `{repo, number}`), so a
  // reader sees WHICH issue went unlinked without joining the ask's row.
  AskIssueLinkSkipped: 'ask.issue_link_skipped',
  // H5 #5403 (plan 101 slice 09): the screen read ONE third-party item a working agent
  // was about to be handed on a push path — a webhook field, a subject comment, a PR
  // comment or reviewer finding. One row per item, `ok` and withheld alike, fresh or a
  // `screen_verdicts` hit, written once the session (or task) it anchors on exists.
  // `input` carries `{ session_id | task_id, item_kind }`; `output` carries `{ verdict,
  // reason, cached }` where `verdict` is `ok` | `discard` | `unavailable` — never the
  // text.
  PromptItemScreened: 'prompt.item_screened',
  // #4400 (epic #4391, Terminal slice 09): the blocker DECIDE lane — the thing
  // that most needs a person — had neither a stream topic nor an audit kind
  // until now (only `blocker.secrets_landed`, one step later, existed).
  // `blocker.asked` fires once per blocker RAISED (`apps/worker/src/fleet/
  // blocker-ingest.ts`'s `ingestBlockedSignal`, wired at its one call site in
  // `apps/worker/src/wiring/nats-consumers.ts`), on the store's `created` verdict
  // only — a redelivery or a re-run into the same wall answers `existing` and
  // audits nothing, which is the edge-trigger the row needs. It used to ride
  // `EscalationFired` as a stated compromise (`blocker.raised` was the name
  // proposed then); this is that promise kept, under the issue's own name.
  BlockerAsked: 'blocker.asked',
  // `blocker.answered` fires once per blocker ANSWERED — `answerBlocker`'s own
  // CAS on `resolution IS NULL` is the edge-trigger, so a second answer attempt
  // (`already_resolved`) audits nothing. Both doors that can close a blocker
  // (`apps/api/src/deps/blockers.ts`'s prose `answer` and the completed-form
  // close in `apps/api/src/deps/blocker-submit.ts`) share the one seam that
  // fires this, so a human's typed answer and a form's last field both read the
  // same way here.
  BlockerAnswered: 'blocker.answered',
  // `blocker.guidance_written` fires once per blocker the GUIDANCE SWEEP wrote steps for
  // (#6679) — `apps/worker/src/cron/blocker-guidance.ts`, through the account-anchored
  // sink. Edge-triggered by `recordBlockerGuidance`'s own CAS on `guidance IS NULL`, so a
  // row another pass won audits nothing, exactly as `blocker.answered` is edge-triggered
  // by `already_resolved`.
  //   WHY IT IS A ROW AT ALL, when the `blockers.guidance` column already carries the
  //   model and the timestamp: the column holds the SURVIVING guidance, and this row holds
  //   the ATTEMPT — including the attempts that produced nothing (`kind: 'none'` with its
  //   reason), which is the half an operator asking "why is this blocker stepless" needs.
  //   It is also a call made on the customer's own BYOK key by a machine nobody asked, the
  //   same reason the intake screen's `prompt.item_screened` is a row.
  //   NAMES AND COUNTS, never the steps: a step is prose a person reads on the blocker,
  //   and an audit row is not a second place to keep it.
  BlockerGuidanceWritten: 'blocker.guidance_written',
  // `blocker.withdrawn` fires once per blocker a CALLER withdrew through
  // `BlockerControl.withdraw` (`apps/api/src/deps/blockers.ts`) — the REST
  // `POST /v1/blockers/:id/withdraw` and the MCP `blocker withdraw` action — and
  // names who did it. `withdrawBlocker`'s CAS on `resolution IS NULL` is the
  // edge-trigger, so a repeat finds no open row and audits nothing. Door-scoped:
  // the automatic closers (the PR-settled sweep, the env-fault lane, the hold
  // release) also set `withdrawn` and do not write it.
  BlockerWithdrawn: 'blocker.withdrawn',
  // arc 101 slice 03 (epic #3756, door 8): access LANDED on a blocker's form — a
  // human (the fix link), a token holder (`POST /v1/blockers/:id/submit`) or an
  // agent (the MCP `blocker submit` verb) supplied some of the fields a
  // `missing_credential` ask named. ONE row per submit that landed something,
  // written by the api's one submit control (`apps/api/src/deps/blocker-submit.ts`)
  // through the account-anchored sink. Beside the raw-task rows because it is the
  // same inlet one step earlier: the submit that completes a form files the raw
  // task those rows then trail.
  //   FIRST-CLASS, not `mcp_tool.call.ok` with the name in `tool`: two of its
  //   three doors are not tool calls, so a row claiming one would misstate how the
  //   access arrived. `tool` is null — `appendEvent` refuses a tool name on a
  //   non-tool-call kind.
  //   **NAMES, never values** (door 4): the field names that landed, the ones
  //   still open, and the door's evidence (the link's `jti` + user agent + IP, or
  //   the token id). The values went to their vaults, which leave their own
  //   metadata-only rows (`repo_secret.stored`, `secret.saved`).
  BlockerSecretsLanded: 'blocker.secrets_landed',
  // arc 101 slice 02: the maintainer moved `repositories.donations_enabled` — the
  // opt-in that lets a public repo RECEIVE donated boxes/keys. Deliberately NOT a
  // `.maintainer.yml` field: a PR author must never be able to change who funds
  // the repo, so it is an owner-authenticated account/billing fact and this row is
  // the only durable trace of the consent moving.
  //   `repo.donations_enabled` — the opt-in that also PUBLISHES this repo's
  //     activity counts + backer wall on a no-auth page. Necessary, not sufficient:
  //     a donatable repo is `enabled AND NOT private AND donations_enabled`, so the
  //     row records the consent, never the eligibility (that conjunction has exactly
  //     one home, in the query layer).
  //   `repo.donations_disabled` — withdrawal. Live `donations` rows PARK rather
  //     than delete (the donor may want them back if the repo returns), so nothing
  //     else in the system records that receiving was switched off.
  // Written by the REST toggle through the account-anchored sink
  // (`buildAccountAudit`), attributed to the acting human, beside that lane's own
  // `project.enabled` / `project.disabled` pair. First-class since #2270: rows before
  // it carry the name in `tool` under `mcp_tool.call.ok`, so a history query reads
  // `kind = $1 OR (kind LIKE 'mcp_tool.call.%' AND tool = $1)`.
  RepoDonationsEnabled: 'repo.donations_enabled',
  RepoDonationsDisabled: 'repo.donations_disabled',
  // arc 101 slice 12 (plan doc 07): the donation LIFECYCLE, written by the api's
  // donations port (`deps/donations.ts`) through the account-anchored sink. A gift
  // hands someone else's hardware and someone else's inference key to a repo its
  // donor does not own, so "audit is trust" bites hardest here — these four were
  // structured stdout only until this slice, which is no record at all once a pod
  // rotates.
  //   Unlike the verbs beside them on that sink, they are FIRST-CLASS kinds rather
  //   than `mcp_tool.call.ok` rows carrying a name in `tool`: a donation is a
  //   two-party fact about money and resources, not a tool call, and a donor asking
  //   "what did my key run, and when did it stop?" must be able to read the answer
  //   off `audit_events.kind` (indexed) without knowing which transport wrote it.
  //   `tool` is therefore null on all four — `appendEvent` refuses a tool name on a
  //   non-tool-call kind.
  //   **Never in a payload**: the API key, the SSH credential, the donor's email.
  //   Login and resource id only (`packages/audit` `redact` scrubs by pattern, but
  //   nothing here puts one in the row to begin with).
  //   `donation.created` — the gift was recorded: kind, target, the pinned repo,
  //     the donated box/key ID, and the donor's `allowed_models` ceiling when it
  //     stamped one (the column it wrote is mutable afterwards, so the row is the
  //     only record of what was authorized AT the gift).
  //   `donation.cancelled` — the DONOR detached it (or a failed checkout mint
  //     retracted a pending `money` pledge, carrying the reason).
  //   `donation.declined` — the RECIPIENT refused it. Keyed to the refusing
  //     maintainer's account with the donor's beside it: a decline is a two-party
  //     event, and the trail has to answer "whose gift, against which project?".
  //   `donation.model_chosen` — the recipient pointed a donated key at a model
  //     inside the donor's ceiling. Carries the model NAME, never the key.
  DonationCreated: 'donation.created',
  DonationCancelled: 'donation.cancelled',
  DonationDeclined: 'donation.declined',
  DonationModelChosen: 'donation.model_chosen',
  // arc 101 slice 10 (plan doc 04): a PENDING `money` gift became live because
  // Stripe said it was paid (`checkout.session.completed` carrying the session's
  // `metadata[donation_id]`). The row is written by the WEBHOOK lane, never by the
  // success redirect — "the donor came back to `?donated=1`" is a URL anyone can
  // type, and this row is what makes "when did this gift start counting, and on
  // whose payment?" answerable. Parent-less (a webhook lane), so it rides the
  // structured job-audit store like the pause/resume/seat-sync rows beside it.
  DonationActivated: 'donation.activated',
  // arc 101 slice 10 (plan doc 04): the donor's subscription ENDED
  // (`customer.subscription.deleted`), so every `compute`/`llm` gift they pinned
  // was parked — earmark cleared, box drained. The $50/mo is the fee for
  // OPERATING the donated box and key, so operation stops when the fee does;
  // without this lane one month's payment buys perpetual operation.
  //   The row carries the Stripe reason (`cancellation_details.reason` — a
  //   cancellation the donor asked for reads differently from a declined card),
  //   which gifts parked, and which boxes the park drained. Nothing else records
  //   it: the parked rows keep their `chosen_model` for a re-attach and the
  //   donation ledger has no "why" column, so this row IS the trail — for the
  //   donor whose resources stopped and the maintainer whose capacity vanished
  //   alike. Written by the WEBHOOK lane, parent-less, through the structured
  //   job-audit store like the seat-sync row it follows.
  DonationParked: 'donation.parked',
  // epic #1259 (the operator write surface): an audited entitlement OVERRIDE was
  // issued, or pulled early. Written by the api's grants port (`deps/grants.ts`)
  // through the account-anchored sink, from BOTH transports it serves (the admin
  // REST surface and the control MCP `grant` verb).
  //   A grant hands FREE capacity — extra ai-dev slots, extra boxes, extra CI
  //   parallelism, or an uncapped fleet — to an org or a person, on the platform's
  //   own margin. That is precisely the act "audit is trust" exists for: the
  //   `accounts.unlimited_fleet` column this table replaced had no issuer, no
  //   reason, no expiry and no revocation, and these two rows are what ends that
  //   silence.
  //   FIRST-CLASS kinds rather than `mcp_tool.call.ok` rows carrying a name in
  //   `tool`, on the `donation.*` precedent beside them: a grant is a two-party
  //   fact about capacity somebody else is not paying for, not a tool call, and
  //   "who gave this org an uncapped fleet, when, and why" has to be readable off
  //   the INDEXED `kind` column without knowing which door wrote it. `tool` is
  //   null on both — `appendEvent` refuses a tool name on a non-tool-call kind.
  //   The row is anchored to the SUBJECT's account when the grant lifts an ORG
  //   (the `account.set_tier` posture: the trail belongs where the capacity
  //   landed) and to the ISSUING operator's account when it lifts a PERSON, who
  //   has no account of their own to anchor to. Either way the payload names the
  //   subject PAIR (`subjectKind` + `subjectId`), so neither anchoring loses the
  //   other party.
  //   `grant.issued`  — kind, amount, source, reason, expiry, and the issuing human.
  //   `grant.revoked` — fired off `revokeGrant`'s CAS boolean, so a second revoke
  //     (a re-run, or a second operator racing the first) stays SILENT rather than
  //     re-announcing a revocation that already happened.
  GrantIssued: 'grant.issued',
  GrantRevoked: 'grant.revoked',
  // #2270 (the note above `AskRedriven`): promoted from `mcp_tool.call.*`.
  // A platform operator changed a CUSTOMER's entitlements or ruled on its OSS
  // verification (`deps/platform-account-overrides.ts`, `platform-oss-verdict.ts`,
  // via `platform-actor-audit.ts`): the row lands on the customer's chain, naming the
  // operator in `input.actor`. Staff's role change on a member rides the same funnel;
  // the org's own role change (`deps/members.ts`) writes the same kind, so a tenant's
  // feed reads one vocabulary whoever changed the role.
  AccountSetTier: 'account.set_tier',
  AccountSetSeats: 'account.set_seats',
  AccountSetSpendCap: 'account.set_spend_cap',
  AccountOssVerifyDecide: 'account.oss_verify_decide',
  OrgMemberRoleChanged: 'org_member.role_changed',
  // #6124: the org's own member trail (`deps/members.ts` `buildOrgMemberAudit`): a
  // member invited, a member removed. `role_changed` above is the third.
  OrgMemberInvited: 'org_member.invited',
  OrgMemberRemoved: 'org_member.removed',
  // #1918's board is a READ of GitHub, so its one write goes through to GitHub:
  // renaming a goal retitles the milestone or the `type/epic` issue itself.
  // Durable because the act is otherwise untraceable from here — GitHub records
  // that the App renamed something, not WHICH operator asked it to, and the
  // board stores nothing of its own to compare against. The row carries both
  // titles, since "renamed" without the before is an event nobody can undo.
  GoalRenamed: 'goal.renamed',
  // #1743: an API credential was MINTED or REVOKED. Written by the api's one
  // `TokenAudit` sink, so every door that mints one leaves the same row — the
  // REST `POST|DELETE /v1/tokens`, the MCP `token` verb, and the OAuth grant
  // leg, which deliberately ends in the same `ApiTokenStore.create`.
  //   Until now these two landed on `console.log` and nowhere else, while the
  //   grants, repo, KB and BYOK mutations beside them went durable. An operator
  //   reading `audit_events` after an incident could see who was granted uncapped
  //   capacity but not that a token able to spend it had been minted — and the
  //   only copy of that fact died with the pod. A credential's birth and death are
  //   the first two rows an incident review looks for.
  //   FIRST-CLASS kinds rather than `mcp_tool.call.ok` carrying the name in
  //   `tool`, on the `grant.*` / `donation.*` precedent: two of the three doors
  //   are not tool calls at all, so a row claiming an MCP call would be a false
  //   statement about how the credential was minted. `tool` is null on both —
  //   `appendEvent` refuses a tool name on a non-tool-call kind.
  //   The row names the token id, its scopes and the acting human; the SECRET
  //   never reaches it (only `api_tokens.prefix` is even readable after the mint).
  TokenCreated: 'token.created',
  TokenRevoked: 'token.revoked',
  //   A token's LABEL changed — not its reach. Audited beside the birth and the
  //   death because the label is what an operator identifies a credential by on
  //   the one screen that lists them, so a rename is the one edit that can make
  //   a later trail read about a token nobody recognises. The row carries the
  //   name it moved from and to; there is no scope change to record, because
  //   this door cannot make one.
  TokenRenamed: 'token.renamed',
  // #2270 (the note above `AskRedriven`): promoted from `mcp_tool.call.*`.
  // The account's box templates and config keys set / deleted (`deps/templates.ts`).
  TemplateSet: 'template.set',
  TemplateDeleted: 'template.deleted',
  ConfigSet: 'config.set',
  ConfigDeleted: 'config.deleted',
  // #5920: the operator-token writer door's STEP-UP refusal — a cookie session
  // whose login is older than the window the door enforces. Stamped on the
  // SAME row as the credential mints and revokes because the trace the issue
  // wants is exactly that: a stolen-cookie replay at the writer door is the
  // signal #2891 exists for, and without this kind a stolen session leaves
  // nothing but the 403. The row names the session id, the human userId
  // behind it, the login instant and the age in ms — no `accountId`, no
  // `tokenId`, because there is no credential to name and no account context
  // to anchor the row against; `appendEvent`'s `accountId` validation lets
  // the row through when the column is null and the caller said so.
  OperatorTokenStepupRefused: 'operator_token.stepup_refused',
  // #6124 (the #2270 note above `AskRedriven`): the account sites #2270 left on
  // `mcp_tool.call.*`, each value the name the site wrote in `tool`. Err-only names
  // kept their value (`blocker.submit`, `push.subscribe`, the `*_unnudged` alerts,
  // `access_request.claim_lost`, `artifact.storage_unusable`, `invitation.refused`).
  // `artifact.shared`'s failed member fan-out got an err sibling,
  // `artifact.share_notify_failed`, and the #2270 note's one query is WRONG for the
  // pair: old fan-out failures are `mcp_tool.call.err` rows naming `artifact.shared` in
  // `tool`. History reads:
  //   artifact.shared:              kind = 'artifact.shared'
  //                                 OR (kind = 'mcp_tool.call.ok' AND tool = 'artifact.shared')
  //   artifact.share_notify_failed: kind = 'artifact.share_notify_failed'
  //                                 OR (kind = 'mcp_tool.call.err' AND tool = 'artifact.shared')
  // The waitlist decisions (`deps/access-requests.ts`): invited, declined, and the
  // claim lost after the effects landed.
  AccessRequestInvited: 'access_request.invited',
  AccessRequestDeclined: 'access_request.declined',
  AccessRequestClaimLost: 'access_request.claim_lost',
  // Invitations (`deps/invitations.ts`, `routes/admin-invitations.ts`): an org mint
  // refused, minted, revoked, accepted, re-sent.
  InvitationRefused: 'invitation.refused',
  InvitationMinted: 'invitation.minted',
  InvitationRevoked: 'invitation.revoked',
  InvitationAccepted: 'invitation.accepted',
  InvitationResent: 'invitation.resent',
  // A booked invitation's capacity was REFUSED at accept: consumed, NOT approved
  // (plan 102 slice 05, `apps/api/src/deps/invitations.ts` `buildBookingFulfil`).
  InvitationFulfilRefused: 'invitation.fulfil_refused',
  // An invitation consumed by someone ALREADY a member of its account — written by BOTH
  // doors that consume one: the login-time consume and the token accept (`redeemWithRow`).
  // The db's `already_a_member` verdict with `consumed: true` stamps `accepted_at` and
  // grants nothing new, so the consume is its own row rather than an
  // `invitation.accepted` that would read as a membership granted.
  InvitationAlreadyMember: 'invitation.already_a_member',
  // Blockers: a form submit that landed nothing (`deps/blocker-submit.ts`), and an
  // answer whose resumption ask could not be nudged (`deps/blockers.ts`).
  BlockerSubmit: 'blocker.submit',
  BlockerRawTaskUnnudged: 'blocker.raw_task_unnudged',
  // A key-outage drain whose held work could not be nudged (`routes/key-outage.ts`).
  KeyOutageDrainUnnudged: 'key_outage.drain_unnudged',
  // An operator re-drove a dead key-label audit intent
  // (`packages/audit/src/key-label-intent.ts`, #5279).
  KeyLabelAuditRedrive: 'key_label_audit.redrive',
  // The account's configuration: secrets, outbound webhooks and webhook sources,
  // the LLM-call log sink, system prompts, dashboards, the OSS verification ask.
  SecretSaved: 'secret.saved',
  SecretDeleted: 'secret.deleted',
  // #7785: one escalation closed by id (`POST /v1/escalations/:id/ack`).
  EscalationAcked: 'escalation.acked',
  // #7785: a repo secret's store and delete, the repo-scoped twins of the two above. NAMES and
  // environments only, plus which names may reach a box's disk; never a value or ciphertext.
  RepoSecretStored: 'repo_secret.stored',
  RepoSecretDeleted: 'repo_secret.deleted',
  WebhookCreated: 'webhook.created',
  WebhookDeleted: 'webhook.deleted',
  WebhookTested: 'webhook.tested',
  WebhookDeleteRefused: 'webhook.delete_refused',
  WebhookSourceConnected: 'webhook_source.connected',
  WebhookSourceRotated: 'webhook_source.rotated',
  // A connected source's drop rules were replaced in place (`integration source_update`).
  WebhookSourceUpdated: 'webhook_source.updated',
  WebhookSourceDisconnected: 'webhook_source.disconnected',
  // #6167: a webhook source's `status` / `last_error` MOVED. Two writers, one kind:
  // ingest's delivery tracker (`trackSourceHealth`, `apps/ingest/src/sources.ts`) and the
  // `gitlab.mr_hint` lane's read health (`recordReadHealth`, `apps/worker`). Written only
  // when the compare-and-set LANDED and its pre-image (`RETURNING old.*`, `packages/db`
  // `webhook-sources.ts`) differs from what it wrote, so a lost race, a no-op and a
  // retried job or redelivered hint write none. Tenant-anchored on the source's account,
  // `visibleToMaintainer`. Output: `WebhookSourceHealthChangedOutputSchema` (`./payload`) —
  // source id, from, to, writer; never a payload or a secret. The CAUSE keeps its own row
  // (`inbound.received`, `forge.mr.observe_refused` / `forge.mr.observed`).
  WebhookSourceHealthChanged: 'webhook_source.health_changed',
  LogSinkSet: 'log_sink.set',
  LogSinkCleared: 'log_sink.cleared',
  SystemPromptSet: 'system_prompt.set',
  SystemPromptCleared: 'system_prompt.cleared',
  DashboardUpserted: 'dashboard.upserted',
  DashboardUpdated: 'dashboard.updated',
  DashboardDeleted: 'dashboard.deleted',
  OssVerificationApplied: 'oss_verification.applied',
  // Where the account's mail and pushes go: email routes, notification preferences,
  // a test email, the hard-bounce retry, an unsubscribe, a push subscription refused.
  EmailRouteSet: 'email_route.set',
  EmailRouteReset: 'email_route.reset',
  EmailRouteResetNoop: 'email_route.reset_noop',
  NotificationPrefSet: 'notification_pref.set',
  NotificationPrefReset: 'notification_pref.reset',
  NotificationPrefReplaced: 'notification_pref.replaced',
  NotificationTestEmail: 'notification.test_email',
  EmailStatusRetry: 'email_status.retry',
  EmailUnsubscribed: 'email.unsubscribed',
  PushSubscribe: 'push.subscribe',
  // The artifact store (`artifacts/store.ts`): put, shared, deleted; the BYO bucket
  // unusable; and a share whose member fan-out failed to enqueue.
  ArtifactPut: 'artifact.put',
  ArtifactShared: 'artifact.shared',
  ArtifactDeleted: 'artifact.deleted',
  // The delete REFUSED: the storage side rejected the object's DELETE, so the
  // bytes SURVIVE and the metadata row is KEPT — it carries the object key and
  // the bucket they live in, so the expiry reaper (or a caller retry) can still
  // remove them. Its own kind, because `artifact.deleted` must mean "a row was
  // really removed": the row that says the delete did NOT happen is a different
  // fact, and an operator asking "which deletes failed" must be able to read it
  // off the indexed `kind` column.
  ArtifactDeleteFailed: 'artifact.delete_failed',
  ArtifactStorageUnusable: 'artifact.storage_unusable',
  ArtifactShareNotifyFailed: 'artifact.share_notify_failed',
} as const;
